import React, { useState, useMemo } from "react";
import { ChevronRight, Network, Search } from "lucide-react";
import { ENTITY_ORDER, ENTITY_COLORS, ENTITY_LABELS } from "@/lib/entityColors";
import { Input } from "@/components/ui/input";

// Persistent right-hand "text explorer" — browse every KG entity grouped by type.
export function GraphSidebar({ nodes, onSelect, activeId }) {
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState({});

  const groups = useMemo(() => {
    const term = q.trim().toLowerCase();
    return ENTITY_ORDER.map((type) => ({
      type,
      label: ENTITY_LABELS[type],
      nodes: nodes
        .filter((n) => n.type === type && (term ? n.label.toLowerCase().includes(term) : true))
        .sort((a, b) => a.label.localeCompare(b.label)),
    }));
  }, [nodes, q]);

  const toggle = (t) => setCollapsed((p) => ({ ...p, [t]: !p[t] }));

  return (
    <aside
      className="hidden lg:flex flex-col w-80 shrink-0 border-l border-[#14140f]/10 bg-[#fdfdfb]"
      data-testid="graph-sidebar"
    >
      <div className="flex items-center gap-2 px-5 h-14 border-b border-[#14140f]/10 shrink-0">
        <Network className="w-4 h-4 text-[#c23c28]" />
        <h3 className="font-serif text-lg text-[#14140f]">Knowledge Graph</h3>
      </div>
      <div className="p-3 border-b border-[#14140f]/10 shrink-0">
        <div className="relative">
          <Search className="w-4 h-4 text-[#14140f]/35 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search entities…"
            data-testid="sidebar-search-input"
            className="pl-8 h-9 bg-white border-[#14140f]/10"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {groups.every((g) => g.nodes.length === 0) && (
          <p className="text-sm text-[#14140f]/45 px-3 py-6 text-center">No entities match.</p>
        )}
        {groups.map((g) =>
          g.nodes.length ? (
            <div key={g.type} className="mb-0.5">
              <button
                onClick={() => toggle(g.type)}
                data-testid={`sidebar-group-${g.type}`}
                className="w-full flex items-center gap-1.5 px-2 py-2 rounded-md hover:bg-[#f5f4f0] transition-colors duration-200"
              >
                <ChevronRight
                  className={`w-4 h-4 text-[#14140f]/40 transition-transform duration-200 ${
                    collapsed[g.type] ? "" : "rotate-90"
                  }`}
                />
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: ENTITY_COLORS[g.type] }} />
                <span className="flex-1 text-left text-sm font-semibold text-[#14140f]">{g.label}</span>
                <span className="text-xs text-[#14140f]/40">{g.nodes.length}</span>
              </button>
              {!collapsed[g.type] && (
                <div className="ml-3 border-l border-[#14140f]/10 pl-2 py-0.5">
                  {g.nodes.map((n) => (
                    <button
                      key={n.id}
                      onClick={() => onSelect(n.id)}
                      data-testid={`sidebar-node-${n.id}`}
                      className={`w-full text-left text-sm px-2 py-1.5 rounded-md truncate transition-colors duration-200 ${
                        activeId === n.id
                          ? "bg-[#ff7360]/10 text-[#c23c28] font-medium"
                          : "text-[#14140f]/80 hover:bg-[#f5f4f0]"
                      }`}
                    >
                      {n.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : null
        )}
      </div>
    </aside>
  );
}
